import * as MUI from "@mui/material";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React from "react";
import { useTranslation } from "react-i18next";
import DetailField from "../../../../../components/display/DetailField";
import SectionCard from "../../../../../components/display/SectionCard";
import { normalizePetGender } from "../../../../pets/utils/Functions";

function Actions({ donor, onClose, onViewPet }) {
  const { t } = useTranslation();
  return (
    <MUI.Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 2 }}
    >
      <MUI.Tooltip title={t("ViewPet")}>
        <MUI.IconButton onClick={() => onViewPet(donor)} size="small">
          <OpenInNewIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Tooltip>

      <MUI.IconButton onClick={onClose} size="small">
        <CloseIcon fontSize="small" />
      </MUI.IconButton>
    </MUI.Stack>
  );
}

function Title({ donor }) {
  return (
    <MUI.Stack direction="row" spacing={2} alignItems="center">
      <MUI.Avatar
        variant="rounded"
        sx={{
          bgcolor: "error.main",
          width: 52,
          height: 52,
          borderRadius: 2.5,
        }}
      >
        <BloodtypeIcon />
      </MUI.Avatar>
      <MUI.Box sx={{ minWidth: 0 }}>
        <MUI.Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.25 }}
          noWrap
        >
          {donor?.name}
        </MUI.Typography>
        {donor?.bloodType && (
          <MUI.Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <MUI.Chip label={donor.bloodType} size="small" color="primary" />
          </MUI.Stack>
        )}
      </MUI.Box>
    </MUI.Stack>
  );
}

/** View-only: blood-type is set from the pet's own edit form, not here. */
export default function BloodDonorDetailsDialog({ donor, onClose, onViewPet }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));

  const petFields = [
    { label: t("Species"), value: donor?.type?.title },
    { label: t("Gender"), value: t(normalizePetGender(donor?.gender)) },
    { label: t("BirthDate"), value: donor?.birthdate },
  ];
  const ownerFields = [
    { label: t("Owner"), value: donor?.owner },
    { label: t("PhoneNumber"), value: donor?.phoneNumber },
    { label: t("ClinicName"), value: donor?.clinicName },
  ];

  return (
    <MUI.Dialog
      open={Boolean(donor)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle sx={{ p: { xs: 2.5, sm: 3.5 }, pt: { xs: 1, sm: 1.25 } }}>
        <Actions donor={donor} onClose={onClose} onViewPet={onViewPet} />
        <Title donor={donor} />
      </MUI.DialogTitle>

      <MUI.DialogContent
        dividers
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? "grey.50"
              : "rgba(255,255,255,0.02)",
        }}
      >
        <MUI.Stack spacing={2.5}>
          <SectionCard label={t("PetDetails")}>
            <MUI.Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                rowGap: 2,
                columnGap: 2,
              }}
            >
              {petFields.map((field) => (
                <DetailField key={field.label} label={field.label} value={field.value || "-"} />
              ))}
            </MUI.Box>
          </SectionCard>

          <SectionCard label={t("OwnerDetails")}>
            <MUI.Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                rowGap: 2,
                columnGap: 2,
              }}
            >
              {ownerFields.map((field) => (
                <DetailField key={field.label} label={field.label} value={field.value || "-"} />
              ))}
            </MUI.Box>
          </SectionCard>
        </MUI.Stack>
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
